import React, { useEffect } from 'react';

import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { NavigationWrapper } from 'src/app/navigation-wrapper';
import PageLoadingErrorGuard from 'src/components/page-loading-error-guard';
import { DEFAULT_TITLE, TITLE_TEMPLATE } from 'src/constants/helmet';
import { FeatureToggle } from 'src/containers/feature-toggle';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { hasTransitioned } from 'src/router/history';
import { useParams } from 'src/router/hooks';
import {
  LoadRepositoryPage,
  UNLOAD_REPOSITORY,
} from 'src/sections/repository/actions';
import { getCurrentRepositoryFullSlug } from 'src/selectors/repository-selectors';
import { appWasServerSideRendered } from 'src/utils/ssr';

import CloneModalDialog from './containers/clone-modal-dialog';
import SyncDialog from './containers/sync-dialog';
import RepositoryNavigation from './navigation';

type Params = {
  repositoryFullSlug?: string;
  repositoryOwner?: string;
  repositorySlug?: string;
};

const getRepositoryFullSlug = (params: Params) => {
  const { repositoryFullSlug, repositoryOwner, repositorySlug } = params;
  if (typeof repositoryFullSlug === 'string') {
    return repositoryFullSlug;
  } else {
    return `${repositoryOwner}/${repositorySlug}`;
  }
};

const getRepositorySlug = (params: Params) => {
  const { repositoryFullSlug, repositorySlug } = params;
  if (!repositorySlug) {
    return repositoryFullSlug?.split('/')?.[1];
  }

  return repositorySlug;
};

const RepositoryRoute = ({ children }: { children: React.ReactNode }) => {
  const params = useParams<Params>();
  const reduxRepoName = useSelector(getCurrentRepositoryFullSlug);
  const dispatch = useDispatch();
  const fullSlug = getRepositoryFullSlug(params);
  const defaultTitle = `${fullSlug.replace('/', ' / ')} — ${DEFAULT_TITLE}`;

  const tabTitleChangesEnabled = useStatsigGate(
    StatsigFeatureKeys.tabTitleChangesEnabled
  );
  const titleTemplate = tabTitleChangesEnabled
    ? `%s — ${getRepositorySlug(params)} - Bitbucket`
    : `${fullSlug.replace('/', ' / ')} ${TITLE_TEMPLATE}`;

  useEffect(() => {
    // if inital load and the app was SSRed the LoadRepositoryPage.SUCCESS action needs to be dispatched as
    // it was dispatched on the server by a resource but the watching sagas are disabled on the server
    if (appWasServerSideRendered() && !hasTransitioned()) {
      // load the non-critical data when rendering a SSRed repository route
      dispatch({
        type: LoadRepositoryPage.SUCCESS,
        payload: { result: {} },
      });
    }
  }, [dispatch]);

  useEffect(() => {
    // This purpose of this effect is to unload the repository from redux
    // before visiting the page. This prevents a flash of content from a
    // previous repository showing up when navigating to a new repository.
    // It's not ideal that we have to manage this manually. If we don't, there
    // are certain redirects (like on the source browser) that will redirect to
    // the prior repository (still in redux) instead of the new one (using the
    // value from the URL).
    if (!reduxRepoName) {
      // If we don't have a repo in redux, we don't need to unload it
      return;
    }
    if (/\{.*\}/.test(fullSlug)) {
      // If the URL is using a UUID, don't unload from redux. This might not
      // always be right! We assume the only time a UUID is in the URL it's
      // from a fresh page load; none of our client links use UUIDs. At some
      // point during that page load we redirect from UUID -> fullSlug. That
      // updates the dependencies of this effect, but since the repository has
      // already been loaded we don't want to unload it.
      return;
    }
    if (
      fullSlug.localeCompare(reduxRepoName, undefined, {
        sensitivity: 'base',
      }) === 0
    ) {
      // If the URL matches redux (including case insensitive URLs), we don't
      // need to unload.
      // Condition does not distinguish between accented and unaccented
      // characters since repository slugs are composed of only characters
      // that are ASCII alphanumeric.
      return;
    }
    // In all other cases, remove the repository from redux before continuing.
    // We expect this to happen before we try to load the new repository.
    dispatch({ type: UNLOAD_REPOSITORY });
  }, [dispatch, fullSlug, reduxRepoName]);

  const contents = (
    <NavigationWrapper
      Component={RepositoryNavigation}
      mobileHeading={fullSlug}
    >
      <React.Fragment>
        <Helmet
          defer={false}
          defaultTitle={defaultTitle}
          titleTemplate={titleTemplate}
        />
        <CloneModalDialog />
        {
          // @ts-ignore: requires @atlaskit/router to be bumped after AKR-23 released
          <SyncDialog />
        }
        {children}
      </React.Fragment>
    </NavigationWrapper>
  );

  return (
    <FeatureToggle
      fallback={contents}
      feature="fd-repository-page-loading-error-guard"
    >
      <PageLoadingErrorGuard>{contents}</PageLoadingErrorGuard>
    </FeatureToggle>
  );
};

export default RepositoryRoute;
